import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { Card, TabHeading, UpgradeCard } from "./StudioShared";

const LANG_MIXES = ["equilibre", "plus_darija", "plus_francais"];
const SEGMENTS = ["intro", "jingle", "heure-meteo", "dedicace"];

// Trie les voix : masculines d'abord (préférence produit), le reste ensuite.
function sortVoices(voices) {
  return [...voices].sort((a, b) => {
    const am = (a.labels?.gender || "").toLowerCase() === "male" ? 0 : 1;
    const bm = (b.labels?.gender || "").toLowerCase() === "male" ? 0 : 1;
    return am - bm;
  });
}

export default function HostTab({ id, locked }) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null); // config serveur
  const [voices, setVoices] = useState([]);
  const [voicesUnavailable, setVoicesUnavailable] = useState(false);

  const [saving, setSaving] = useState(false);
  const [segment, setSegment] = useState("intro");
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [script, setScript] = useState(null); // { text, source: "preview"|"generate" }
  const [audioSrc, setAudioSrc] = useState(null); // data URI mp3

  // Toast local (même motif que PublicRadio).
  const [toast, setToast] = useState(null); // { type, msg }
  const toastTimer = useRef(null);
  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // Traduit un code d'erreur HTTP en message convivial.
  const errorMessage = useCallback((err) => {
    const status = err?.response?.status;
    if (status === 429) return t("host.errors.cooldown");
    if (status === 502) return t("host.errors.upstream");
    return t("host.errors.generic");
  }, [t]);

  useEffect(() => {
    if (locked) { setLoading(false); return; }
    let mounted = true;
    (async () => {
      await Promise.all([
        api.get(`/api/radios/${id}/host`)
          .then((r) => { if (mounted) setCfg(r.data); })
          .catch(() => { if (mounted) setCfg(null); }),
        api.get(`/api/radios/${id}/host/voices`)
          .then((r) => { if (mounted) setVoices(sortVoices(r.data?.voices || [])); })
          .catch((err) => { if (mounted && err?.response?.status === 503) setVoicesUnavailable(true); }),
      ]);
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [id, locked]);

  if (locked) {
    return <UpgradeCard title={t("host.locked.title")} desc={t("host.locked.desc")} />;
  }
  if (loading) return <p className="text-gray-500">{t("studio.loading")}</p>;

  const set = (k, v) => setCfg((c) => ({ ...(c || {}), [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/api/radios/${id}/host`, {
        enabled: cfg?.enabled ? 1 : 0,
        persona_name: cfg?.persona_name || "",
        lang_mix: cfg?.lang_mix || "equilibre",
        voice_id: cfg?.voice_id || null,
        frequency_songs: Number(cfg?.frequency_songs) || 1,
      });
      setCfg(r.data);
      showToast("success", t("host.saved"));
    } catch (err) {
      showToast("error", errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    setScript(null);
    setAudioSrc(null);
    try {
      const r = await api.post(`/api/radios/${id}/host/preview`, { segmentType: segment });
      setScript({ text: r.data?.script || "", source: "preview" });
      if (r.data?.audioBase64) setAudioSrc(`data:audio/mpeg;base64,${r.data.audioBase64}`);
    } catch (err) {
      showToast("error", errorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/api/radios/${id}/host/generate`, { segmentType: segment });
      setScript({ text: r.data?.script || "", source: "generate" });
      setAudioSrc(null);
      showToast("success", t("host.generatedToast"));
    } catch (err) {
      showToast("error", errorMessage(err));
    } finally {
      setGenerating(false);
    }
  };

  const busy = previewing || generating;

  return (
    <div className="space-y-6">
      <TabHeading title={t("host.title")} subtitle={t("host.subtitle")} />

      {/* Réglages de l'animateur */}
      <Card className="space-y-5">
        {/* Activation */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!cfg?.enabled}
            onChange={(e) => set("enabled", e.target.checked ? 1 : 0)}
            className="mt-1"
          />
          <span>
            <span className="block font-medium text-gray-900">{t("host.enable")}</span>
            <span className="block text-sm text-gray-500">{t("host.enableHint")}</span>
          </span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Nom du persona */}
          <label className="text-xs text-gray-500">
            <span className="block mb-1">{t("host.persona")}</span>
            <input
              value={cfg?.persona_name ?? ""}
              onChange={(e) => set("persona_name", e.target.value)}
              placeholder="Momo"
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
            />
          </label>

          {/* Fréquence */}
          <label className="text-xs text-gray-500">
            <span className="block mb-1">{t("host.frequency")}</span>
            <select
              value={cfg?.frequency_songs || 1}
              onChange={(e) => set("frequency_songs", Number(e.target.value))}
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{t("host.frequencyOption", { count: n })}</option>
              ))}
            </select>
          </label>

          {/* Mélange de langues */}
          <label className="text-xs text-gray-500">
            <span className="block mb-1">{t("host.langMix")}</span>
            <select
              value={cfg?.lang_mix || "equilibre"}
              onChange={(e) => set("lang_mix", e.target.value)}
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
            >
              {LANG_MIXES.map((m) => (
                <option key={m} value={m}>{t(`host.langMixOptions.${m}`)}</option>
              ))}
            </select>
          </label>

          {/* Voix */}
          <label className="text-xs text-gray-500">
            <span className="block mb-1">{t("host.voice")}</span>
            <select
              value={cfg?.voice_id || ""}
              onChange={(e) => set("voice_id", e.target.value || null)}
              disabled={voicesUnavailable || voices.length === 0}
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white disabled:opacity-60"
            >
              <option value="">{t("host.voiceDefault")}</option>
              {voices.map((v) => {
                const meta = [v.labels?.gender, v.labels?.accent].filter(Boolean).join(", ");
                return (
                  <option key={v.voice_id} value={v.voice_id}>
                    {v.name}{meta ? ` — ${meta}` : ""}
                  </option>
                );
              })}
            </select>
            {voicesUnavailable && (
              <span className="block mt-1 text-[11px] text-amber-600">{t("host.voicesUnavailable")}</span>
            )}
          </label>
        </div>

        <div>
          <button
            onClick={save}
            disabled={saving}
            className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-xl font-medium disabled:opacity-50"
          >
            {saving ? t("host.saving") : t("studio.actions.save")}
          </button>
        </div>
      </Card>

      {/* Aperçu & diffusion */}
      <Card className="space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900">{t("host.tryTitle")}</h3>
          <p className="text-sm text-gray-500 mt-1">{t("host.tryDesc")}</p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-500">
            <span className="block mb-1">{t("host.segment")}</span>
            <select
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
            >
              {SEGMENTS.map((s) => (
                <option key={s} value={s}>{t(`host.segments.${s}`)}</option>
              ))}
            </select>
          </label>

          <button
            onClick={preview}
            disabled={busy}
            className="text-sm bg-purple-50 text-purple-700 hover:bg-purple-100 px-4 py-2.5 rounded-xl font-medium disabled:opacity-50"
          >
            {previewing ? t("host.generating") : `▶ ${t("host.preview")}`}
          </button>

          <button
            onClick={generate}
            disabled={busy}
            className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-xl font-medium disabled:opacity-50"
          >
            {generating ? t("host.generating") : `📡 ${t("host.generate")}`}
          </button>
        </div>

        {busy && <p className="text-xs text-gray-400">{t("host.slowNote")}</p>}

        {script && (
          <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 text-white flex items-center justify-center text-sm">🎙️</span>
              <span className="text-sm font-medium text-gray-700">
                {cfg?.persona_name || "Momo"}
              </span>
            </div>
            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{script.text}</p>
            {audioSrc && (
              <audio controls autoPlay src={audioSrc} className="w-full">
                <track kind="captions" />
              </audio>
            )}
            {script.source === "generate" && (
              <p className="text-xs text-emerald-600">{t("host.generatedNote")}</p>
            )}
          </div>
        )}
      </Card>

      {/* Toast */}
      {toast && (
        <div className="fixed inset-x-0 bottom-6 z-50 flex justify-center px-4 pointer-events-none">
          <div className={`pointer-events-auto rounded-xl px-4 py-3 text-sm font-medium shadow-lg ${toast.type === "success" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
